import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import {map, mergeMap} from '@igp/shared/otel';
import {BaseEpicsBuilder} from '@igp/shared/redux';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {messageActions, realtimeActions} from '@redux';
import {inject, injectable} from 'inversify';
import type {Epic} from 'redux-observable';
import {of} from 'rxjs';
import {filter} from 'rxjs/operators';
import {isActionOf} from 'typesafe-actions';

import {referralLinkConfigActions} from './actions';
import {referralLinkConfigEntityType, ReferralLinkConfigServiceTypes} from './constants';
import type {IReferralLinkConfigService} from './service';
import type {ReferralLinkConfigErrorCodes} from './types';

export const localized = defineMessages({
    addReferralLinkConfigSuccess: {
        id: 'ReferralLinkConfig_addReferralLinkConfigSuccess',
        defaultMessage: 'Referral link config added successfully',
    },
    addReferralLinkConfigFailure: {
        id: 'ReferralLinkConfig_addReferralLinkConfigFailure',
        defaultMessage: 'Failed to add referral link config',
    },
    editReferralLinkConfigSuccess: {
        id: 'ReferralLinkConfig_editReferralLinkConfigSuccess',
        defaultMessage: 'Referral link config updated successfully',
    },
    editReferralLinkConfigFailure: {
        id: 'ReferralLinkConfig_editReferralLinkConfigFailure',
        defaultMessage: 'Failed to update referral link config',
    },
    removeReferralLinkConfigSuccess: {
        id: 'ReferralLinkConfig_removeReferralLinkConfigSuccess',
        defaultMessage: 'Referral link config removed successfully',
    },
    removeReferralLinkConfigFailure: {
        id: 'ReferralLinkConfig_removeReferralLinkConfigFailure',
        defaultMessage: 'Failed to remove referral link config',
    },
    configAlreadyExist: {
        id: 'ReferralLinkConfig_configAlreadyExist',
        defaultMessage: 'Config already exists for AAL',
    },
    invalidUrl: {
        id: 'ReferralLinkConfig_invalidUrl',
        defaultMessage: 'Invalid URL is passed',
    },
    configNotFound: {
        id: 'ReferralLinkConfig_configNotFound',
        defaultMessage: 'Config is not found',
    },
});

@injectable()
export class ReferralLinkConfigEpicsBuilder extends BaseEpicsBuilder {
    constructor(@inject(ReferralLinkConfigServiceTypes.service) private readonly _service: IReferralLinkConfigService) {
        super();
    }

    buildEpicList(): Epic[] {
        return [
            this.buildAddRequestEpic(),
            this.buildAddSuccessEpic(),
            this.buildAddFailureEpic(),
            this.buildEditRequestEpic(),
            this.buildEditSuccessEpic(),
            this.buildEditFailureEpic(),
            this.buildRemoveRequestEpic(),
            this.buildRemoveSuccessEpic(),
            this.buildRemoveFailureEpic(),
        ];
    }

    private buildAddRequestEpic(): Epic {
        return this.buildRequestEpic(referralLinkConfigActions.addReferralLinkConfig, payload =>
            this._service.addReferralLinkConfig(payload)
        );
    }

    private buildAddSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.addReferralLinkConfig.success)),
                mergeMap(() => {
                    return of(
                        realtimeActions.event({
                            entity: referralLinkConfigEntityType,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Add},
                        }),
                        messageActions.saveMessage({message: localized.addReferralLinkConfigSuccess, mode: 'success'})
                    );
                })
            );
    }

    private buildAddFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.addReferralLinkConfig.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message: this.errorCodeMapping[errorCode as ReferralLinkConfigErrorCodes] ?? localized.addReferralLinkConfigFailure,
                        mode: 'error',
                    });
                })
            );
    }

    private buildEditRequestEpic(): Epic {
        return this.buildRequestEpic(referralLinkConfigActions.editReferralLinkConfig, payload =>
            this._service.editReferralLinkConfig(payload)
        );
    }

    private buildEditSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.editReferralLinkConfig.success)),
                mergeMap(() => {
                    return of(
                        realtimeActions.event({
                            entity: referralLinkConfigEntityType,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Update},
                        }),
                        messageActions.saveMessage({message: localized.editReferralLinkConfigSuccess, mode: 'success'})
                    );
                })
            );
    }

    private buildEditFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.editReferralLinkConfig.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message:
                            this.errorCodeMapping[errorCode as ReferralLinkConfigErrorCodes] ?? localized.editReferralLinkConfigFailure,
                        mode: 'error',
                    });
                })
            );
    }

    private buildRemoveRequestEpic(): Epic {
        return this.buildRequestEpic(referralLinkConfigActions.removeReferralLinkConfig, payload =>
            this._service.removeReferralLinkConfig(payload)
        );
    }

    private buildRemoveSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.removeReferralLinkConfig.success)),
                mergeMap(() => {
                    return of(
                        realtimeActions.event({
                            entity: referralLinkConfigEntityType,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Delete},
                        }),
                        messageActions.saveMessage({message: localized.removeReferralLinkConfigSuccess, mode: 'success'})
                    );
                })
            );
    }

    private buildRemoveFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(referralLinkConfigActions.removeReferralLinkConfig.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message:
                            this.errorCodeMapping[errorCode as ReferralLinkConfigErrorCodes] ?? localized.removeReferralLinkConfigFailure,
                        mode: 'error',
                    });
                })
            );
    }

    private errorCodeMapping: Record<ReferralLinkConfigErrorCodes, MessageDescriptor> = {
        CONFIG_ALREADY_EXIST: localized.configAlreadyExist,
        INVALID_URL: localized.invalidUrl,
        NOT_FOUND: localized.configNotFound,
    };
}
